import { useState, useEffect, useRef } from "react"
import { useSidebar } from "../contexts/sidebar-context"
import { useAuth } from "../contexts/auth-context"
import { TranslatedText } from "./translated-text"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible"
import {
  Building2,
  BarChart3,
  Users,
  FileText,
  Settings,
  Brain,
  AlertTriangle,
  CreditCard,
  PieChart,
  Calculator,
  ChevronRight,
  Home,
  ShoppingCart,
  Receipt,
  Banknote,
  Package,
  UserCheck,
  FolderOpen,
  Globe,
  HelpCircle,
  TrendingUp,
  Shield,
  Target,
  Clock,
  Zap,
  DollarSign,
  FileBarChart,
  MessageSquare,
  BookOpen,
  ShoppingBag,
  Wallet,
  Building,
  UserCog,
  Hotel,
  Wine,
  Utensils,
  X,
} from "lucide-react"
import { cn, formatRole } from "../lib/utils"
import { Link, useLocation } from "react-router-dom"
import { useKeyboardNavigation } from "./keyboard-navigation-provider"

interface NavigationItem {
  name: string
  icon: any
  href: string
  current?: boolean
  favorite?: boolean
  recent?: boolean
  badge?: string
  adminOnly?: boolean
  roles?: string[]
}

interface NavigationGroup {
  name: string
  icon: any
  items: NavigationItem[]
  defaultExpanded?: boolean
  priority?: "core" | "optional" | "utility"
}

const adaptiveMenuConfig = {
  core: ["Dashboard", "Sales & Invoicing", "Inventory & Stock", "Purchases & Expenses", "Banking & Cash", "Tax Management"],
  optional: ["Inventory & Products", "Payroll & HR", "Projects & Time", "Multi-Company", "International"],
  utility: [
    "Reports & Analytics",
    "AI Insights",
    "AI Powered Features",
    "Settings",
    "Help & Support",
    "Documents",
    "Client Portal",
    "Security",
  ],
}

const userPreferences = {
  enabledModules: new Set([
    "Dashboard",
    "Sales & Invoicing",
    "Inventory & Stock",
    "Purchases & Expenses",
    "Banking & Cash",
    "Inventory & Products",
    "Accounting",
    "Fixed Assets",
    "Journal Entries Hub",
    "Tax Management",
    "Reports & Analytics",
    "AI Insights",
    "AI Powered Features",
    "Client Portal",
    "System Settings",
  ]),
  favoriteItems: new Set([
    "Financial Overview",
    "Drinks Inventory (Bar)",
    "Kitchen & Food Inventory",
    "AI Smart Insights",
    "AI Powered Features",
    "Client Portal",
    "Invoices",
    "Expenses",
    "Accounts Payable",
    "Journal Entries Hub",
    "Bank Accounts & Reconciliation",
    "Enhanced Financial Reports",
    "Advanced Analytics Dashboard",
    "AI Financial Coach",
    "Fixed Assets",
  ]),
  recentlyUsed: ["Invoices", "Expenses", "Accounts Payable", "AI Smart Insights", "AI Powered Features", "Client Portal", "Bank Accounts & Reconciliation", "Enhanced Financial Reports", "Advanced Analytics Dashboard", "AI Financial Coach", "Fixed Assets"],
}

// Standalone navigation items (not grouped)
const standaloneItems: NavigationItem[] = [
  { 
    name: "Dashboard", 
    icon: Home, 
    href: "/dashboard",
    favorite: true
  },
  { 
    name: "Banking & Cash", 
    icon: Banknote, 
    href: "/dashboard/banking",
    favorite: true
  },
  { 
    name: "Point of Sale", 
    icon: CreditCard, 
    href: "/dashboard/pos",
    favorite: true
  },
  { 
    name: "Hotel Management", 
    icon: Hotel, 
    href: "/dashboard/hotel"
  },
]

// Navigation groups for better organization
const navigationGroups: NavigationGroup[] = [
  {
    name: "Sales & Revenue",
    icon: ShoppingCart,
    items: [
      { 
        name: "Sales & Invoicing", 
        icon: ShoppingCart, 
        href: "/dashboard/sales"
      },

    ],
    defaultExpanded: true,
    priority: "core"
  },
  {
    name: "Inventory & Stock",
    icon: Package,
    items: [
      { 
        name: "Drinks Inventory (Bar)", 
        icon: Wine, 
        href: "/dashboard/inventory",
        favorite: true,
        recent: true
      },
      { 
        name: "Kitchen & Food Inventory", 
        icon: Utensils, 
        href: "/dashboard/food-inventory",
        favorite: true,
        recent: true
      },
    ],
    defaultExpanded: true,
    priority: "core"
  },
  {
    name: "Purchases & Expenses",
    icon: Receipt,
    items: [
      { 
        name: "Expenses & Purchases", 
        icon: Receipt, 
        href: "/dashboard/expenses",
        favorite: true,
        recent: true
      },
      { 
        name: "Accounts Payable", 
        icon: Wallet, 
        href: "/dashboard/enhanced-accounts-payable",
        favorite: true
      },
      { 
        name: "Purchase Orders", 
        icon: ShoppingBag, 
        href: "/dashboard/purchase-orders",
        favorite: true
      },
      { 
        name: "Supplier Portal", 
        icon: Building2, 
        href: "/dashboard/vendors",
        favorite: true
      },
    ],
    defaultExpanded: true,
    priority: "core"
  },

  // {
  //   name: "Accounting",
  //   icon: FileText,
  //   items: [
  //     { 
  //       name: "Accounting", 
  //       icon: FileText, 
  //       href: "/dashboard/accounting",
  //       favorite: true
  //     },
  //     { 
  //       name: "Journal Entries Hub", 
  //       icon: BookOpen, 
  //       href: "/dashboard/journal-hub",
  //       favorite: true
  //     },
  //   ],
  //   defaultExpanded: true,
  //   priority: "core"
  // },
  // {
  //   name: "Assets & HR",
  //   icon: Building,
  //   items: [
  //     { 
  //       name: "Fixed Assets", 
  //       icon: Building, 
  //       href: "/dashboard/fixed-assets",
  //       favorite: true
  //     },
  //     { 
  //       name: "Payroll Management", 
  //       icon: Users, 
  //       href: "/dashboard/payroll",
  //       favorite: true
  //     },
  //   ],
  //   defaultExpanded: false,
  //   priority: "optional"
  // },
  {
    name: "Settings",
    icon: Settings,
    items: [
      { 
        name: "Settings", 
        icon: Settings, 
        href: "/dashboard/settings",
        adminOnly: true
      },
      { 
        name: "Company", 
        icon: Building2, 
        href: "/dashboard/companies"
      },
      { 
        name: "Users", 
        icon: UserCog, 
        href: "/dashboard/users",
        adminOnly: true
      },
    ],
    defaultExpanded: false,
    priority: "utility"
  },
]

// Flattened navigation items for backward compatibility
const navigationItems: NavigationItem[] = [
  ...standaloneItems,
  ...navigationGroups.flatMap(group => group.items)
]
  // Commented out items - not shown in sidebar
  // { 
  //   name: "Supplier Portal", 
  //   icon: Building2, 
  //   href: "/dashboard/supplier-portal",
  //   badge: "New",
  //   favorite: true
  // },
  // { 
  //   name: "Tax Management", 
  //   icon: Calculator, 
  //   href: "/dashboard/tax",
  //   badge: "Enhanced"
  // },
  // { 
  //   name: "Tax Calculation", 
  //   icon: Calculator, 
  //   href: "/dashboard/tax-calculation",
  //   badge: "New"
  // },
  // { 
  //   name: "Approval Hub", 
  //   icon: UserCheck, 
  //   href: "/dashboard/approval-hub",
  //   badge: "New",
  //   favorite: true
  // },
  // { 
  //   name: "Smart Ledger", 
  //   icon: MessageSquare, 
  //   href: "/dashboard/enhanced-journal-management",
  //   badge: "Smart"
  // },
  // { 
  //   name: "Reports & Analytics", 
  //   icon: BarChart3, 
  //   href: "/dashboard/reports",
  //   favorite: true
  // },
  // { 
  //   name: "AI Insights", 
  //   icon: Brain, 
  //   href: "/dashboard/ai-insights",
  //   badge: "AI",
  //   favorite: true
  // },
  // { 
  //   name: "AI Powered Features", 
  //   icon: Zap, 
  //   href: "/dashboard/llama-ai",
  //   badge: "AI+",
  //   favorite: true
  // },
  // { 
  //   name: "Documents", 
  //   icon: FolderOpen, 
  //   href: "/dashboard/documents"
  // },
  // { 
  //   name: "Client Portal", 
  //   icon: Users, 
  //   href: "/dashboard/clients",
  //   badge: "Portal",
  //   favorite: true
  // },
  // { 
  //   name: "Settings", 
  //   icon: Settings, 
  //   href: "/dashboard/settings"
  // },
  // { 
  //   name: "Help & Support", 
  //   icon: HelpCircle, 
  //   href: "/dashboard/help-support"
  // }

export function Sidebar() {
  const location = useLocation()
  const { isCollapsed, setIsCollapsed, isOpen, closeSidebar } = useSidebar()
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(navigationGroups.filter(g => g.defaultExpanded).map(g => g.name))
  )
  const { setFocusedElement } = useKeyboardNavigation()
  const { user } = useAuth()

  // Close sidebar drawer on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        closeSidebar()
      }
    }
    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [isOpen])
  
  // Filter standalone items based on user role

  const filteredStandaloneItems = standaloneItems.filter(item => {
    // Show admin-only items only if user is admin
    const userRole = user?.role as string;
    if (item.adminOnly && userRole !== 'super_admin' && userRole !== 'admin') {
      return false
    }
    
    // Hide standalone items for waiters and receptionists (they only see Hotel Management)
    if (user?.role === 'waiter' || user?.role === 'receptionist') {
      return false
    }

    return true
  })
  
  // Filter navigation groups based on user role
  const filteredNavigationGroups = navigationGroups.map(group => ({
    ...group,
    items: group.items.filter(item => {
      // Show admin-only items only if user is admin
      const userRole = user?.role as string;
    if (item.adminOnly && userRole !== 'super_admin' && userRole !== 'admin') {
        return false
      }

      // WAITER & RECEPTIONIST RESTRICTION: Check access scope
      if (user?.role === 'waiter' || user?.role === 'receptionist') {
        const hasPosAccess = (user as any).accessScope?.includes('POS');
        const hasHotelAccess = (user as any).accessScope?.includes('HOTEL');

        if (group.name === 'Sales & Revenue' && item.name === 'Point of Sale' && hasPosAccess) {
          return true
        }

        if (group.name === 'Operations' && item.name === 'Hotel Management' && hasHotelAccess) {
          return true
        }

        // Explicitly return false for everything else for these roles
        return false
      }

      return true
    })
  })).filter(group => group.items.length > 0)

  // Filter navigation items based on user role (for backward compatibility)
  const filteredNavigationItems = navigationItems.filter(item => {
    const userRole = user?.role as string;

    // STRICT RESTRICTION: Waiters and Receptionists see specific items only
    if (userRole === 'waiter' || userRole === 'receptionist') {
      const hasPosAccess = (user as any).accessScope?.includes('POS');
      const hasHotelAccess = (user as any).accessScope?.includes('HOTEL');

      if (item.name === 'Point of Sale' && hasPosAccess) return true;
      if (item.name === 'Hotel Management' && hasHotelAccess) return true;

      // Receptionists usually have inherent Hotel access
      if (userRole === 'receptionist' && item.name === 'Hotel Management') return true;

      return false;
    }

    // Hide redundant items for Admin/Manager (they use floating buttons)
    if (['admin', 'super_admin', 'manager'].includes(userRole)) {
       const redundantItems = ['Point of Sale', 'Hotel Management', 'Inventory & Products', 'Purchase Orders'];
       if (redundantItems.includes(item.name)) return false;
    }

    // Show admin-only items only if user is admin
    if (item.adminOnly && userRole !== 'super_admin' && userRole !== 'admin') {
      return false
    }
    return true
  })
  
  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement?.closest("[data-sidebar]")) {
        const allItems = document.querySelectorAll("[data-sidebar-item]")

        if (e.key === "ArrowDown") {
          e.preventDefault()
          const nextIndex = Math.min(focusedIndex + 1, allItems.length - 1)
          setFocusedIndex(nextIndex)
          ;(allItems[nextIndex] as HTMLElement)?.focus()
        } else if (e.key === "ArrowUp") {
          e.preventDefault()
          const prevIndex = Math.max(focusedIndex - 1, 0)
          setFocusedIndex(prevIndex)
          ;(allItems[prevIndex] as HTMLElement)?.focus()
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [focusedIndex])



  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-[9999] flex flex-col w-72 h-screen min-h-screen max-h-screen transition-transform duration-300 ease-in-out border-r border-border bg-card text-card-foreground shadow-xl",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}
      data-sidebar
      role="navigation"
      aria-label="Main navigation"
      aria-hidden={!isOpen}
    >
      <div className="p-4 border-b border-border flex-shrink-0 bg-background/50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center shrink-0 rounded-lg bg-muted p-1 border border-border">
            <img 
              src="/brand-e.svg" 
              alt="Monsterado Lounge" 
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-bold text-foreground tracking-tight whitespace-nowrap">Monsterado Lounge</h2>
            <p className="text-xs text-muted-foreground whitespace-nowrap">Restaurant & Bar</p>
          </div>
          <button
            onClick={closeSidebar}
            className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1 sidebar-scrollbar min-h-0 max-h-full" role="navigation" aria-label="Main menu">
        {filteredNavigationItems.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href?.includes('?') && location.pathname === item.href?.split('?')[0])
          
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-3.5 py-2.5 text-sm font-medium rounded-lg transition-colors border-0 outline-none",
                isActive
                  ? "bg-[#742425] text-white font-semibold shadow-sm"
                  : "text-foreground/80 hover:bg-muted hover:text-foreground"
              )}
              data-sidebar-item
              aria-current={isActive ? "page" : undefined}
              aria-label={item.name}
              onFocus={() => setFocusedElement("nav-item")}
            >
              <item.icon className={cn("w-4 h-4 flex-shrink-0", isActive ? "text-white" : "text-muted-foreground")} />
              <span className="flex-1 truncate">
                <TranslatedText text={item.name} />
              </span>
            </Link>
          )
        })}
      </nav>

      <div className="p-3.5 border-t border-border flex-shrink-0 bg-background/50">
        <div className="bg-muted/60 p-3 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: '#742425' }}>
              {(() => {
                const fullName = (user as any)?.fullName || (user as any)?.name
                if (fullName) {
                  const parts = fullName.trim().split(' ')
                  if (parts.length >= 2) {
                    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
                  }
                  return fullName.charAt(0).toUpperCase()
                }
                if (user?.firstName && user?.lastName) {
                  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                }
                if (user?.firstName) {
                  return user.firstName.charAt(0).toUpperCase()
                }
                if (user?.email) {
                  return user.email.charAt(0).toUpperCase()
                }
                return 'U'
              })()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {(user as any)?.fullName || (user as any)?.name || 
                 (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                 user?.firstName ||
                 (user?.email ? user.email.split('@')[0] : 'User')}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {formatRole(user?.role)}
              </p>
            </div>
            <button
              className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground transition-colors"
              aria-label="User settings"
              data-sidebar-item
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}
